import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Grid,
  Skeleton,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import fileDownload from "js-file-download";
import { decode } from "base64-arraybuffer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModeEditOutline from "@mui/icons-material/ModeEditOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCredentials from "../../../components/common/infonavit/addCredentials/addCredentials";
import UploadFile from "../../../components/common/UploadFile/uploadFile";
import DownloadTemplate from "../../../components/common/UploadFile/downloadTemplate";
import { getUser, getUserStatus } from "../../userSlice";
import { NoInfoComponent } from "../../../components/common/NoInfoImage/noInfoComponent";
import ModalInfoTraders from "../../../components/common/Modals/modalInfoTraders";
import { ModalDecision } from "../../../components/common/Modals/modalDecision";
import CustomTablePagination from "../../../components/DataTable/CustomTablePagination";
import { DownloadCredentialsTemplate } from "../../../services/user/traders/mastersTradersService";
import {
  GetCredentials,
  UploadCredentialsFile,
  DeleteCredential,
} from "../../../services/user/infonavit/adminCredentials";
import { CODES } from "../../../consts/codes";
import { useNavigate } from "react-router-dom";

export const AdminCredentials = () => {
  /**
   * Use Navigate
   */
  const navigate = useNavigate();

  /**
   * Use Translation
   */
  const { t } = useTranslation();

  /**
   * Use Selector
   */
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  /**
   * use State
   */
  // Data de grilla
  const [data, setData] = useState([]);

  // Archivo seleccionado
  const [fileSelected, setFileSelected] = useState(null);

  // Carga de tabla o refrescar
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Modal respuesta carga credenciales
  const [isOpenModalResponse, setIsOpenModalResponse] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState({
    data: { responseMessage: "" },
  });

  // Edit credentials
  const [openEditCredentials, setOpenEditCredentials] = useState(false);
  const [currentCredentials, setCurrentCredentials] = useState({});

  // Modal confirmar eliminación
  const [openDeleteCredentials, setOpenDeleteCredentials] = useState(false);

  /**
   * Use Effect
   */
  /**
   * Carga la información de la tabla
   */
  useEffect(() => {
    if (userStatus === "succeeded") fetchData();
  }, [userStatus]);

  /**
   * Configuración para las columnas de la tabla
   */
  const columns = [
    {
      name: "companyName",
      label: t("App.infonavitUploadCredentials.table.razonSocial"),
      options: {},
    },
    {
      name: "rfc",
      label: t("App.infonavitUploadCredentials.table.rfc"),
      options: {},
    },
    {
      name: "consult",
      label: "",
      options: {
        customHeadLabelRender: () => (
          <p>
            {t("App.infonavitUploadCredentials.table.consultNRP1")}
            <br />
            {t("App.infonavitUploadCredentials.table.consultNRP2")}
          </p>
        ),
        customBodyRenderLite: (dataIndex) => {
          return (
            <IconButton
              onClick={() => {
                handleGoToNRPs(data[dataIndex]);
              }}
            >
              <VisibilityIcon fontSize={"24px"} style={{ color: "#543ab4" }} />
            </IconButton>
          );
        },
      },
    },
    {
      name: "edit",
      label: t("App.infonavitUploadCredentials.table.edit"),
      options: {
        customBodyRenderLite: (dataIndex) => (
          <IconButton
            onClick={() => {
              handleEditCredentials(data[dataIndex]);
            }}
          >
            <ModeEditOutline fontSize={"24px"} style={{ color: "#543ab4" }} />
          </IconButton>
        ),
      },
    },
    {
      name: "delete",
      label: t("App.infonavitUploadCredentials.table.delete"),
      options: {
        customBodyRenderLite: (dataIndex) => (
          <IconButton
            onClick={() => {
              handleDeleteCredentials(data[dataIndex]);
            }}
          >
            <DeleteOutlineIcon fontSize={"24px"} style={{ color: "#543ab4" }} />
          </IconButton>
        ),
      },
    },
  ];

  /**
   * Opciones para la configuración de la tabla
   */
  const options = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    viewColumns: false,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  /**
   * Handles
   */
  /**
   * Consulta las credenciales del cliente
   */
  const fetchData = async () => {
    try {
      setData([]);
      setIsLoadingData(true);

      const mainUser = userInfo.mainUser;
      const {
        status,
        data: { responseStatus, response },
      } = await GetCredentials(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setData(response);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Descarga la plantilla de carga de credenciales
   */
  const handleDownloadTemplate = async () => {
    try {
      const {
        status,
        data: { responseMessage, responseCode, data },
      } = await DownloadCredentialsTemplate();

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST
      ) {
        fileDownload(decode(data.fileContent), data.fileName);
      } else {
        setModalTitle(handleSetTitle(responseCode));
        setModalContent({
          status: status,
          data: {
            responseMessage,
            responseCode,
          },
        });
        setIsOpenModalResponse(true);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Carga el archivo de credenciales
   */
  const handleUploadFile = async () => {
    try {
      const obj = {
        fileContent: fileSelected[0],
        fileName: fileSelected[0].name,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await UploadCredentialsFile(obj);

      setFileSelected(null);
      setModalTitle(handleSetTitle(responseStatus.statusCode));
      setModalContent({ data: { responseStatus, response }, status });

      setIsOpenModalResponse(true);
      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        await fetchData();
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Elimina una credencial
   */
  const deleteCredential = async () => {
    try {
      setOpenDeleteCredentials(false);
      setIsLoadingData(true);

      const {
        status,
        data: { responseStatus, response },
      } = await DeleteCredential(userInfo.mainUser, currentCredentials?.rfc);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setIsLoadingData(false);
        await fetchData();
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      setIsLoadingData(false);
      console.log("====================================");
    }
  };

  /**
   * Retorna el título correspondiente del modal según el responseCode
   * @param {*} responseCode código de respuesta
   * @returns título del modal
   */
  const handleSetTitle = (responseCode) => {
    switch (responseCode) {
      case CODES.S_COD_RESPONSE_SUCCESS_REQUEST:
      case CODES.COD_RESPONSE_SUCCESS:
        return t("App.infonavitUploadCredentials.modal.uploadSuccess");
      default:
        return t("App.validationMessages.error");
    }
  };

  const handleEditCredentials = (payload) => {
    setCurrentCredentials(payload);
    setOpenEditCredentials(true);
  };

  const handleDeleteCredentials = (payload = null) => {
    setCurrentCredentials(payload);
    setOpenDeleteCredentials(true);
  };

  const handleGoToNRPs = (data) =>
    navigate(`/user/configuration/credentials/${data.rfc}`);

  return (
    <Grid
      container
      direction="column"
      rowSpacing={2}
      padding={{
        xs: "2.3rem 1.8rem",
        sm: "3rem 2.25rem",
        md: "3rem 2.25rem",
        lg: "5.25rem 4.5rem",
        xl: "5.25rem 4.5rem",
      }}
    >
      <Grid item>
        <Grid
          container
          rowSpacing={2}
          style={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Grid item className="display-large__primary-one">
            {t("App.infonavitUploadCredentials.title")}
          </Grid>

          <Grid item>
            <AddCredentials fetchCredentials={fetchData} />
          </Grid>
        </Grid>
      </Grid>

      <Grid item className="full-width">
        <Accordion className="accordion__upload" defaultExpanded>
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#543ab4" }} />
            }
          >
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              columnSpacing={2}
            >
              <Grid item>
                <FileUploadOutlinedIcon className="accordion__upload__uploadIcon" />
              </Grid>
              <Grid item md={9} sm={9} xs={9}>
                <p className="display-small__primary-one">
                  {t("App.infonavitUploadCredentials.uploadHeader")}
                </p>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className="accordion__upload__detail">
            <Grid
              container
              direction="column"
              rowSpacing={2}
              className="full-width"
            >
              <Grid item className="full-width">
                <Grid
                  container
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  columnSpacing={2}
                  rowSpacing={2}
                  textAlign={"center"}
                >
                  <DownloadTemplate
                    helpText={t("App.infonavitUploadCredentials.templateText")}
                    buttonText={t("App.catalogue.templateDownload")}
                    handleDownloadTemplate={handleDownloadTemplate}
                  />
                </Grid>
              </Grid>

              <Grid item className="full-width">
                <UploadFile
                  dragText={t("App.infonavitUploadCredentials.dragging")}
                  handleUploadFile={handleUploadFile}
                  fileSelected={fileSelected}
                  setFileSelected={setFileSelected}
                  setErrorModal={(value) => {
                    setModalContent({
                      status: CODES.COD_RESPONSE_HTTP_ERROR,
                      data: { responseMessage: "" },
                    });
                    
                    setIsOpenModalResponse(value);
                  }}
                  setErrorTitle={setModalTitle}
                  customValidation={{
                    allowedSize: 1005760,
                    allowedTypes: ["xls", "xlsx"],
                    requirements: t(
                      "App.infonavitUploadCredentials.requirements"
                    ),
                    requirementsAdvice: t(
                      "App.infonavitUploadCredentials.requirementsAdvice"
                    ),
                  }}
                />
              </Grid>

              <Grid item>
                <Grid
                  container
                  columnSpacing={2}
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Grid item>
                    {" "}
                    <TableChartOutlinedIcon className="accordion__upload__tableIcon" />
                  </Grid>
                  <Grid item className="display-small__primary-one">
                    {" "}
                    {t("App.infonavitUploadCredentials.subtitle")}
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                className="full-width"
                style={{ marginBottom: "1.5rem" }}
              >
                <MUIDataTable
                  data={data}
                  columns={columns}
                  options={options}
                  components={{
                    TableToolbar(props) {
                      return null;
                    },
                    TableFooter: (props) => (
                      <CustomTablePagination paginationProps={props} />
                    ),
                  }}
                  className="dataTable__base datatable-style"
                />
              </Grid>

              <ModalInfoTraders
                title={modalTitle}
                responseData={modalContent}
                open={isOpenModalResponse}
                onClose={() => {
                  setIsOpenModalResponse(false);
                }}
                currentMaster={t("App.traders.masters.credentials")}
              />

              <ModalDecision
                isOpen={openDeleteCredentials}
                setIsOpen={setOpenDeleteCredentials}
                message={t(
                  "App.infonavitUploadCredentials.modal.confirmDeleteModal",
                  {
                    name: currentCredentials?.companyName,
                  }
                )}
                handleAgree={deleteCredential}
                handleDisagree={() => setOpenDeleteCredentials(false)}
                title={t(
                  "App.infonavitUploadCredentials.modal.titleDeleteModal"
                )}
                agreeText={t("App.infonavitUploadCredentials.modal.yes")}
                disagreeText={t("App.infonavitUploadCredentials.modal.no")}
              />

              <AddCredentials
                fetchCredentials={fetchData}
                openEditCredentials={openEditCredentials}
                setOpenEditCredentials={setOpenEditCredentials}
                setCurrentCredentials={setCurrentCredentials}
                currentCredentials={currentCredentials}
              />
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};
